import React, { FunctionComponent } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { IReferenceCandidateForm } from '../../../../common/models/reference/IReferenceCandidateForm';
import { referenceWorkDates, messages, badge } from './utils';
import { IBindingCallback1, IBindingAction } from '../../../../common/models/callback';
import CustomPopover from '../../../../components/CustomPopover';
import { setEditReferenceRoutine, deleteReferenceRoutine } from '../../routines';
import { IPopoverChild } from '../../../../common/models/common/IPopoverChild';
import { ReactComponent as More } from '../../../../assets/icons/more.svg';
import { ReactComponent as PaperPlane } from '../../../../assets/icons/paper-plane.svg';
import { ReactComponent as Pencil } from '../../../../assets/icons/pencil.svg';
import { ReactComponent as Bin } from '../../../../assets/icons/bin.svg';
import styles from './styles.module.scss';
import { formatPhoneNumber } from '../../../../common/helpers/phoneHelper';
import { SurveyStatus } from '../../../../common/enums/SurveyStatus';
import EmptyFieldPlaceholder from '../../../../components/EmptyFieldPlaceholder';

interface IReferenceCardProps {
  reference: IReferenceCandidateForm;
  showProgress?: boolean;
  onEditReference: IBindingCallback1<IReferenceCandidateForm>;
  onDeleteReference: IBindingCallback1<string>;
  onResend?: IBindingCallback1<IReferenceCandidateForm>;
}

const ReferenceCard: FunctionComponent<IReferenceCardProps> = ({
  reference,
  showProgress = false,
  onEditReference,
  onDeleteReference,
  onResend
}) => {
  const updateRef = (hidePopover: IBindingAction) => {
    onEditReference(reference);
    hidePopover();
  };

  const resend = (hidePopover: IBindingAction) => {
    if (onResend) {
      onResend(reference);
    }
    hidePopover();
  };

  const deleteRef = (hidePopover: IBindingAction) => {
    onDeleteReference(reference.id as string);
    hidePopover();
  };

  const Menu = ({ hidePopover }: IPopoverChild) => (
    <>
      <div role="presentation" className={styles.menuItem} onClick={() => hidePopover && updateRef(hidePopover)}>
        <Pencil className={styles.menuIcon} />
        <FormattedMessage id="candidateForm.edit.title" defaultMessage="Edit reference" />
      </div>
      {reference.id?.length === 36
        ? (onResend && reference.status === SurveyStatus.Requested && (
          <div role="presentation" className={styles.menuItem} onClick={() => hidePopover && resend(hidePopover)}>
            <PaperPlane className={styles.menuIcon} />
            <FormattedMessage id="candidateForm.menu.resend" defaultMessage="Resend email" />
          </div>
        ))
        : (
          <div role="presentation" className={styles.menuItem} onClick={() => hidePopover && deleteRef(hidePopover)}>
            <Bin className={styles.menuIcon} />
            <FormattedMessage id="candidateForm.menu.delete" defaultMessage="Delete reference" />
          </div>
        )}
    </>
  );

  return (
    <div className={styles.card}>
      <header className={styles.header}>
        {`${reference.firstName} ${reference.lastName}`}
        <div className={styles.refProgress}>{showProgress && badge(reference.status)}</div>
        {reference.status !== SurveyStatus.Finished && (
          <CustomPopover
            rootClose
            showArrow
            popoverClassName={styles.menu}
            trigger={<div><More className={styles.icon} /></div>}
          >
            <Menu />
          </CustomPopover>
        )}
      </header>
      <div className={styles.body}>
        {reference?.email && (
        <>
          <div className={`${styles.text} ${styles.title}`}><FormattedMessage id="email" defaultMessage="Email" /></div>
          <div className={styles.text}>{reference.email}</div>
        </>
        )}
        {reference?.phoneNumber && (
        <>
          <div className={`${styles.text} ${styles.title}`}><FormattedMessage id="phone" defaultMessage="Phone" /></div>
          <div className={styles.text}>{formatPhoneNumber(reference.phoneNumber)}</div>
        </>
        )}
        <div className={`${styles.text} ${styles.title}`}>
          <FormattedMessage id="position" defaultMessage="Position" />
        </div>
        <div className={styles.text}>
          {reference.type
            ? (
              <FormattedMessage {...messages[reference.type]}>
                {type => (
                  <FormattedMessage
                    id="candidateForm.card.position"
                    defaultMessage="{type} at {workInCompany}"
                    values={{ workInCompany: reference.workInCompany, type }}
                  />
                )}
              </FormattedMessage>
            )
            : <EmptyFieldPlaceholder />}
        </div>
        <div className={`${styles.text} ${styles.title}`}><FormattedMessage id="time" defaultMessage="Time" /></div>
        <div className={styles.text}>
          {reference.workStartDate
            ? referenceWorkDates(reference.workStartDate, reference.workEndDate)
            : <EmptyFieldPlaceholder />}
        </div>
      </div>
    </div>
  );
};

const mapDispachToProps = {
  onEditReference: setEditReferenceRoutine,
  onDeleteReference: deleteReferenceRoutine
};

export default connect(null, mapDispachToProps)(ReferenceCard);
